"use client";

import React from "react";
import { IconComponent } from "@repo/ui/components/icons";
import { getLocalizedText } from "@/themes/default/templates/section/utils";
import type { SectionProps } from "@/themes/default/templates/section/types";

/**
 * Crystal Feature List — two rendering modes over the crystal-night canvas:
 *
 *  • Stat mode (`showIcons === false`): a bordered glass row of big prism
 *    numbers + muted labels (the mockup's "By the Numbers" tiles).
 *  • Card mode (`showIcons !== false`): a grid of frosted-glass cards, each
 *    with a prism top-bar, icon, title, and description (the mockup's
 *    "What We Do" pillars / "Why Crystal Image" grid).
 */
export function FeatureListSection({
  section,
  currentLanguage = "en",
}: SectionProps<any>) {
  const s = section as any;
  const eyebrow = s.title ? getLocalizedText(s.title, currentLanguage) : "";
  const headline = s.headline ? getLocalizedText(s.headline, currentLanguage) : "";
  const description = s.description
    ? getLocalizedText(s.description, currentLanguage)
    : "";
  const features: any[] = Array.isArray(s.features) ? s.features : [];
  const columns = Math.min(Math.max(Number(s.columns) || 3, 1), 4);
  const statMode = s.showIcons === false;

  if (features.length === 0) return null;

  const colClass =
    columns === 4
      ? "sm:grid-cols-2 lg:grid-cols-4"
      : columns === 2
        ? "sm:grid-cols-2"
        : columns === 1
          ? "grid-cols-1"
          : "sm:grid-cols-2 lg:grid-cols-3";

  return (
    <section
      className="px-4 py-16 sm:px-6 md:py-24 lg:px-8"
      data-section-id={section._id}
      data-section-type={section.type}
    >
      <div className="mx-auto max-w-7xl">
        {(eyebrow || headline || description) && (
          <div className="mb-12 max-w-2xl">
            {eyebrow && <span className="crystal-eyebrow">{eyebrow}</span>}
            {headline && (
              <h2
                className="crystal-display mt-3 text-3xl md:text-4xl"
                style={{ color: "var(--color-foreground)", textWrap: "balance" }}
              >
                {headline}
              </h2>
            )}
            {description && (
              <p
                className="mt-4 text-base leading-relaxed md:text-lg"
                style={{ color: "var(--color-muted-foreground)" }}
              >
                {description}
              </p>
            )}
          </div>
        )}

        {statMode ? (
          <div
            className="grid grid-cols-1 overflow-hidden rounded-2xl border sm:grid-cols-3"
            style={{ borderColor: "var(--color-border)" }}
          >
            {features.map((f, i) => (
              <div
                key={i}
                className="p-8"
                style={{
                  backgroundColor: "var(--color-card)",
                  borderLeft:
                    i > 0 ? "1px solid var(--color-border)" : undefined,
                }}
              >
                <div className="crystal-display crystal-prism-text text-4xl font-light md:text-5xl">
                  {getLocalizedText(f.title, currentLanguage)}
                </div>
                <div
                  className="mt-2 text-sm"
                  style={{ color: "var(--color-muted-foreground)" }}
                >
                  {getLocalizedText(f.description, currentLanguage)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`grid grid-cols-1 gap-6 ${colClass}`}>
            {features.map((f, i) => (
              <article
                key={i}
                className="crystal-glass group relative overflow-hidden rounded-2xl p-7 transition-transform hover:-translate-y-1"
              >
                <span
                  className="mb-6 block h-1 w-14 rounded-full"
                  style={{ background: "var(--gradient-primary)" }}
                  aria-hidden="true"
                />
                {f.icon && (
                  <div
                    className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl"
                    style={{
                      background: "rgba(124,147,245,0.12)",
                      color: "var(--color-primary)",
                    }}
                  >
                    <IconComponent name={f.icon} size={22} />
                  </div>
                )}
                <h3
                  className="text-lg font-semibold md:text-xl"
                  style={{ color: "var(--color-foreground)" }}
                >
                  {getLocalizedText(f.title, currentLanguage)}
                </h3>
                {f.description && (
                  <p
                    className="mt-3 text-[15px] leading-relaxed"
                    style={{ color: "var(--color-muted-foreground)" }}
                  >
                    {getLocalizedText(f.description, currentLanguage)}
                  </p>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default FeatureListSection;
